import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import {
  handleMutationError,
  handleQueryError,
  queryRetryPolicy,
} from '@lib/api/serverStateErrorHandler';
import { attachServerStateInstrumentation } from './serverStateInstrumentation';

/**
 * Global QueryClient defaults (docs/DATA_FLOW.md §3, "Query client"). Extracted from
 * App.tsx so the policy is unit-testable.
 */
export const queryClientDefaults = {
  queries: {
    // W3: refetch-on-focus is redundant in a socket-synced app and produces a thundering
    // herd of GETs every time the user alt-tabs back. Socket sync + a 5-min staleTime keep
    // data fresh; opt back in per-query only where genuinely needed.
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Retry only transient 5xx (capped); never 4xx / 401 / 403 — see serverStateErrorHandler.
    retry: queryRetryPolicy,
  },
  mutations: {
    // Mutations are not idempotent by default — never auto-retry.
    retry: 0,
  },
} as const;

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    // Centralized error → toast (docs/DATA_FLOW.md §3, "Error/toast policy"). Queries AND mutations are silent by
    // default in this rollout; they opt in via `meta.showErrorToast` / `meta.errorMessage`
    // (avoids double-toasting call sites that already handle their own errors). 401/403 are
    // left to the axios interceptor's forced logout.
    queryCache: new QueryCache({
      onError: (error, query) => handleQueryError(error, query.meta),
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => handleMutationError(error, mutation.options.meta),
    }),
    defaultOptions: queryClientDefaults,
  });

export const queryClient = createQueryClient();

// M0: dev-only fetch/mutation counters on `window.__serverStateMetrics`. No-op in prod.
attachServerStateInstrumentation(queryClient);
